import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
    Users, GraduationCap, BookOpen, Building, UserPlus, CreditCard,
    Clock, TrendingUp, AlertCircle, CheckCircle, Activity, PhoneCall, Loader2,
    Archive, RefreshCw, Upload, Download, FileSpreadsheet
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ title, value, icon: Icon, trend, color = 'primary' }) => {
    const colorClasses = {
        primary: 'bg-[#064E3B]/10 text-[#064E3B]',
        accent: 'bg-[#D97706]/10 text-[#D97706]',
        success: 'bg-[#10B981]/10 text-[#10B981]',
        warning: 'bg-[#F59E0B]/10 text-[#F59E0B]',
        error: 'bg-[#EF4444]/10 text-[#EF4444]'
    };

    return (
        <Card className="card-surface">
            <CardContent className="p-6">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-sm text-[#4B5563] mb-1">{title}</p>
                        <p className="text-3xl font-bold text-[#1F2937] tabular-nums">{value}</p>
                        {trend && (
                            <p className={`text-xs mt-1 flex items-center ${trend > 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                                <TrendingUp className={`h-3 w-3 mr-1 ${trend < 0 && 'rotate-180'}`} />
                                {Math.abs(trend)}% from last month
                            </p>
                        )}
                    </div>
                    <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
                        <Icon className="h-6 w-6" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [transferOpen, setTransferOpen] = useState(false);
    const [transferPreview, setTransferPreview] = useState(null);
    const [transferConfirm, setTransferConfirm] = useState('');
    const [transferring, setTransferring] = useState(false);
    const [availableYears, setAvailableYears] = useState([]);
    const [currentYear, setCurrentYear] = useState('');
    // Year archive Excel upload
    const [archiveUploadOpen, setArchiveUploadOpen] = useState(false);
    const [archiveUploading, setArchiveUploading] = useState(false);
    const [archiveUploadResult, setArchiveUploadResult] = useState(null);
    const archiveFileRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [statsRes, yearsRes, settingsRes] = await Promise.all([
                    axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/year-archives/years`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/settings`, { withCredentials: true }),
                ]);
                setStats(statsRes.data);
                setAvailableYears(yearsRes.data || []);
                setCurrentYear(settingsRes.data?.current_school_year || '2025-2026');
            } catch (error) {
                console.error('Failed to fetch data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const openTransfer = async () => {
        try {
            const r = await axios.get(`${API_URL}/api/year-transfer/preview`, { withCredentials: true });
            setTransferPreview(r.data);
            setTransferConfirm('');
            setTransferOpen(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to load year transfer preview');
        }
    };

    const executeTransfer = async () => {
        if (transferConfirm !== 'CONFIRM TRANSFER') {
            toast.error('Please type CONFIRM TRANSFER exactly to proceed');
            return;
        }
        setTransferring(true);
        try {
            const r = await axios.post(`${API_URL}/api/year-transfer/execute`, {
                new_year_label: transferPreview?.next_year || '',
                confirm_token: 'CONFIRM TRANSFER',
            }, { withCredentials: true });
            toast.success(r.data.message || 'Year transfer complete');
            setTransferOpen(false);
            // Refresh
            const [statsRes, yearsRes, settingsRes] = await Promise.all([
                axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true }),
                axios.get(`${API_URL}/api/year-archives/years`, { withCredentials: true }),
                axios.get(`${API_URL}/api/settings`, { withCredentials: true }),
            ]);
            setStats(statsRes.data);
            setAvailableYears(yearsRes.data || []);
            setCurrentYear(settingsRes.data?.current_school_year || '');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Year transfer failed');
        } finally { setTransferring(false); }
    };

    const downloadArchiveTemplate = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/year-archives/upload/template`, {
                withCredentials: true, responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'year_archive_template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch { toast.error('Failed to download template'); }
    };

    const handleArchiveUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            toast.error('Please upload an Excel file (.xlsx or .xls)');
            return;
        }
        setArchiveUploading(true);
        setArchiveUploadResult(null);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const r = await axios.post(`${API_URL}/api/year-archives/upload`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setArchiveUploadResult(r.data);
            const okCount = (r.data.created || 0) + (r.data.updated || 0);
            if (okCount > 0) {
                toast.success(`${okCount} guardian archive(s) saved`);
                // Refresh archived years
                const yearsRes = await axios.get(`${API_URL}/api/year-archives/years`, { withCredentials: true });
                setAvailableYears(yearsRes.data || []);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Upload failed');
        } finally {
            setArchiveUploading(false);
            if (archiveFileRef.current) archiveFileRef.current.value = '';
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="admin-dashboard">
                {/* Welcome Section */}
                <div className="bg-gradient-to-r from-[#064E3B] to-[#047857] rounded-xl p-6 text-white">
                    <h1 className="text-2xl font-bold font-['Outfit'] mb-2">Welcome back, Admin!</h1>
                    <p className="text-white/80">Here's what's happening at Nurulhuda Quranic School today.</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                        title="Total Students"
                        value={stats?.total_students || 0}
                        icon={GraduationCap}
                        color="primary"
                    />
                    <StatCard
                        title="Total Teachers"
                        value={stats?.total_teachers || 0}
                        icon={BookOpen}
                        color="accent"
                    />
                    <StatCard
                        title="Total Families"
                        value={stats?.total_parents || 0}
                        icon={Users}
                        color="success"
                    />
                    <StatCard
                        title="Waiting List"
                        value={stats?.waiting_list || 0}
                        icon={UserPlus}
                        color="warning"
                    />
                </div>

                {/* Secondary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <StatCard
                        title="Programs"
                        value={stats?.total_programs || 0}
                        icon={Building}
                        color="primary"
                    />
                    <StatCard
                        title="Classes"
                        value={stats?.total_classes || 0}
                        icon={Activity}
                        color="accent"
                    />
                    <StatCard
                        title="Pending Leave Requests"
                        value={stats?.pending_leave_requests || 0}
                        icon={Clock}
                        color={stats?.pending_leave_requests > 0 ? 'warning' : 'success'}
                    />
                </div>

                {/* Quick Actions & Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Quick Actions */}
                    <Card className="card-surface">
                        <CardHeader>
                            <CardTitle className="font-['Outfit']">Quick Actions</CardTitle>
                            <CardDescription>Common administrative tasks</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <Link to="/admin/waiting-list">
                                <Button variant="outline" className="w-full justify-start" data-testid="quick-action-waiting-list">
                                    <UserPlus className="h-4 w-4 mr-2" />
                                    Review Waiting List
                                    {stats?.waiting_list > 0 && (
                                        <span className="ml-auto bg-[#D97706] text-white text-xs px-2 py-0.5 rounded-full">
                                            {stats.waiting_list}
                                        </span>
                                    )}
                                </Button>
                            </Link>
                            <Link to="/admin/leave-requests">
                                <Button variant="outline" className="w-full justify-start" data-testid="quick-action-leave">
                                    <Clock className="h-4 w-4 mr-2" />
                                    Manage Leave Requests
                                    {stats?.pending_leave_requests > 0 && (
                                        <span className="ml-auto bg-[#F59E0B] text-white text-xs px-2 py-0.5 rounded-full">
                                            {stats.pending_leave_requests}
                                        </span>
                                    )}
                                </Button>
                            </Link>
                            <Link to="/admin/students">
                                <Button variant="outline" className="w-full justify-start" data-testid="quick-action-students">
                                    <GraduationCap className="h-4 w-4 mr-2" />
                                    Manage Students
                                </Button>
                            </Link>
                            <Link to="/admin/payments">
                                <Button variant="outline" className="w-full justify-start" data-testid="quick-action-payments">
                                    <CreditCard className="h-4 w-4 mr-2" />
                                    View Payments
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>

                    {/* Recent Payments */}
                    <Card className="card-surface">
                        <CardHeader>
                            <CardTitle className="font-['Outfit']">Recent Payments</CardTitle>
                            <CardDescription>Latest payment transactions</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {stats?.recent_payments?.length > 0 ? (
                                <div className="space-y-3">
                                    {stats.recent_payments.map((payment) => (
                                        <div key={payment.id || payment.receipt_number} className="flex items-center justify-between p-3 bg-[#FAF9F6] rounded-lg">
                                            <div className="flex items-center space-x-3">
                                                <div className={`p-2 rounded-full ${payment.status === 'completed' ? 'bg-[#10B981]/10' : 'bg-[#F59E0B]/10'}`}>
                                                    {payment.status === 'completed' ? (
                                                        <CheckCircle className="h-4 w-4 text-[#10B981]" />
                                                    ) : (
                                                        <AlertCircle className="h-4 w-4 text-[#F59E0B]" />
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-[#1F2937]">
                                                        {payment.receipt_number || 'Payment'}
                                                    </p>
                                                    <p className="text-xs text-[#4B5563] capitalize">
                                                        {payment.payment_type} • {payment.method}
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="font-semibold text-[#1F2937] tabular-nums">
                                                €{payment.amount?.toFixed(2)}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-[#4B5563] text-center py-8">No recent payments</p>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* School Year Management (Super Admin only) */}
                <Card className="card-surface border-2 border-[#7C3AED]/20" data-testid="year-transfer-card">
                    <CardHeader>
                        <CardTitle className="font-['Outfit'] flex items-center gap-2">
                            <Archive className="h-5 w-5 text-[#7C3AED]" />
                            School Year Management
                        </CardTitle>
                        <CardDescription>Current year: <span className="font-semibold text-[#1F2937]">{currentYear}</span></CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex flex-wrap gap-3">
                            <Button className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" onClick={openTransfer} data-testid="open-year-transfer-btn">
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Start New Year Transfer
                            </Button>
                            <Button variant="outline" onClick={() => { setArchiveUploadResult(null); setArchiveUploadOpen(true); }} data-testid="upload-previous-year-btn">
                                <Upload className="h-4 w-4 mr-2" />
                                Upload Previous Year (Excel)
                            </Button>
                            {availableYears.length > 0 && availableYears.map(y => (
                                <Button key={y} variant="outline" onClick={() => navigate(`/admin/archive/${encodeURIComponent(y)}`)} data-testid={`open-archive-${y}`}>
                                    <Archive className="h-4 w-4 mr-2" />Archive {y}
                                </Button>
                            ))}
                        </div>
                        <p className="text-xs text-[#6B7280]">
                            The transfer archives the current year (receipts, discounts, class assignments) and opens a fresh year for renewed &amp; unrenewed families. Renewal &amp; registration receipts stay live; outstanding balances carry as previous-year debt.
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Year Transfer Dialog */}
            <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-['Outfit'] flex items-center gap-2"><RefreshCw className="h-5 w-5 text-[#7C3AED]" />Start New Year Transfer</DialogTitle>
                    </DialogHeader>
                    {transferPreview && (
                        <div className="space-y-3 text-sm">
                            <div className="bg-[#FFFBEB] border-l-4 border-[#F59E0B] p-3 rounded">
                                <p className="text-[#92400E] font-medium">This action is permanent.</p>
                                <p className="text-[#92400E] text-xs mt-1">Archives <strong>{transferPreview.current_year}</strong> and opens <strong>{transferPreview.next_year}</strong>.</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <MiniStat label="Parents affected" value={transferPreview.parents_affected} />
                                <MiniStat label="Active students" value={transferPreview.students_active} />
                                <MiniStat label="Renewed" value={transferPreview.students_renewed} color="text-[#10B981]" />
                                <MiniStat label="Unrenewed" value={transferPreview.students_unrenewed} color="text-[#F59E0B]" />
                                <MiniStat label="Receipts to archive" value={transferPreview.payments_to_archive} />
                                <MiniStat label="Activities to archive" value={transferPreview.activities_to_archive} />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="transfer-confirm">Type <code className="text-xs bg-[#F3F4F6] px-1 rounded">CONFIRM TRANSFER</code> to enable the button.</Label>
                                <Input id="transfer-confirm" value={transferConfirm} onChange={(e) => setTransferConfirm(e.target.value)} placeholder="CONFIRM TRANSFER" data-testid="transfer-confirm-input" />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTransferOpen(false)}>Cancel</Button>
                        <Button
                            className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
                            disabled={transferring || transferConfirm !== 'CONFIRM TRANSFER'}
                            onClick={executeTransfer}
                            data-testid="execute-year-transfer-btn"
                        >
                            {transferring ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                            Execute Transfer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Previous Year Archive Upload Dialog */}
            <Dialog open={archiveUploadOpen} onOpenChange={setArchiveUploadOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-['Outfit'] flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-[#7C3AED]" />Upload Previous Year Archive</DialogTitle>
                        <DialogDescription>Import historical Guardians, Students and Receipts from a 3-sheet Excel workbook.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="bg-[#F3E8FF] border border-[#DDD6FE] rounded-lg p-4 text-sm text-[#5B21B6]">
                            <p className="font-medium mb-1">Historical data importer</p>
                            <p>Upload a 3-sheet workbook: <b>Guardians</b>, <b>Students</b>, <b>Receipts</b>. Rows are grouped by <code>year</code> + <code>guardian_code</code>. Any remaining balance becomes a &ldquo;Previous debt&rdquo; carried to the current year.</p>
                        </div>
                        <Button variant="outline" className="w-full" onClick={downloadArchiveTemplate} data-testid="download-archive-template">
                            <Download className="h-4 w-4 mr-2" /> Download Template
                        </Button>
                        <div className="border-2 border-dashed border-[#D1D5DB] rounded-lg p-6 text-center">
                            <input
                                ref={archiveFileRef}
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleArchiveUpload}
                                className="hidden"
                                id="archive-file-upload"
                                data-testid="archive-file-input"
                            />
                            <label htmlFor="archive-file-upload" className="cursor-pointer">
                                {archiveUploading ? (
                                    <div className="flex flex-col items-center">
                                        <Loader2 className="h-8 w-8 animate-spin text-[#7C3AED] mb-2" />
                                        <p className="text-sm text-[#4B5563]">Uploading...</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center">
                                        <Upload className="h-8 w-8 text-[#4B5563] mb-2" />
                                        <p className="text-sm font-medium text-[#1F2937]">Click to upload Excel file</p>
                                        <p className="text-xs text-[#4B5563] mt-1">.xlsx or .xls files only</p>
                                    </div>
                                )}
                            </label>
                        </div>
                        {archiveUploadResult && (
                            <div className="space-y-2 text-sm" data-testid="archive-upload-result">
                                <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#10B981]" />{archiveUploadResult.created || 0} created · {archiveUploadResult.updated || 0} updated</div>
                                {archiveUploadResult.students_matched != null && (
                                    <div className="text-xs text-[#4B5563] pl-6">
                                        Students: <span className="text-[#10B981] font-medium">{archiveUploadResult.students_matched} matched</span>
                                        {archiveUploadResult.students_unmatched > 0 && <> · <span className="text-[#F59E0B] font-medium">{archiveUploadResult.students_unmatched} unmatched</span></>}
                                        {' · '}Receipts: <span className="text-[#10B981] font-medium">{archiveUploadResult.receipts_matched} matched</span>
                                        {archiveUploadResult.receipts_unmatched > 0 && <> · <span className="text-[#F59E0B] font-medium">{archiveUploadResult.receipts_unmatched} unmatched</span></>}
                                    </div>
                                )}
                                {archiveUploadResult.skipped > 0 && <div className="flex items-center gap-2 text-[#F59E0B]"><AlertCircle className="h-4 w-4" />{archiveUploadResult.skipped} skipped</div>}
                                {archiveUploadResult.errors?.length > 0 && (
                                    <details className="mt-2" open>
                                        <summary className="cursor-pointer text-xs text-[#EF4444] font-medium">{archiveUploadResult.errors.length} warning(s) — click to review</summary>
                                        <div className="mt-2 max-h-40 overflow-y-auto text-xs text-[#EF4444] space-y-1 bg-[#FEF2F2] rounded p-2">
                                            {archiveUploadResult.errors.slice(0, 20).map((e, i) => <div key={i}>• {e}</div>)}
                                        </div>
                                    </details>
                                )}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
};

const MiniStat = ({ label, value, color }) => (
    <div className="bg-[#FAF9F6] rounded-lg p-3">
        <p className="text-xs text-[#6B7280]">{label}</p>
        <p className={`text-lg font-bold tabular-nums ${color || 'text-[#1F2937]'}`}>{value}</p>
    </div>
);

export default AdminDashboard;
